import { useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Button from "@/v2-backoffice-client/components/button"
import { useTranslationSemanticMutation } from "@/v2-backoffice-client/translation/duplicates/translation.semantic.mutation"
import TranslationDuplicateGroupDialog from "@/v2-backoffice-client/translation/duplicates/TranslationDuplicateGroupDialog"
import {
  buildFuzzyGroups,
  toDuplicateGroup,
} from "@/v2-backoffice-client/translation/duplicates/translation.duplicates.utils"
import type {
  DuplicateGroup,
  TranslationBoard,
} from "@/v2-backoffice-client/translation/translation.types"

const DUPLICATE_COLUMNS = createColumns<DuplicateGroup>({
  enableSorting: true,
  columns: {
    name: { id: "name", label: "Text (en-GB)" },
    source: { id: "source", label: "Match", defaultSize: 80 },
    count: { id: "count", label: "Uses", defaultSize: 70 },
    codesText: {
      id: "codesText",
      label: "Codes",
      renderCell({ row }) {
        return (
          <span className="whitespace-pre-line">{row.original.codesText}</span>
        )
      },
    },
  },
})

export type TranslationDuplicatesViewProps = Omit<
  TableProps<DuplicateGroup>,
  "data" | "columns"
> & {
  board: TranslationBoard
  loading: boolean
}

function TranslationDuplicatesView(props: TranslationDuplicatesViewProps) {
  const { className, board, loading, ...tableProps } = props

  // The fuzzy clustering (edit distance + union-find) is the tab's heaviest
  // client-side computation, so it runs only while this view is mounted and
  // only when the catalog rows actually change. Features only: duplicates
  // resolve by linking into common.FeatureLinks, which type values (data
  // labels sharing a name legitimately, e.g. two enums with an "Other"
  // value) have no equivalent of.
  const featureRows = useMemo(
    () => board.rows.filter((row) => row.objectType === "Feature"),
    [board.rows],
  )
  const fuzzyGroups = useMemo(
    () => buildFuzzyGroups(featureRows),
    [featureRows],
  )

  const [aiGroups, setAiGroups] = useState<DuplicateGroup[]>([])
  const [selected, setSelected] = useState<DuplicateGroup | null>(null)
  const [scanMsg, setScanMsg] = useState<string | null>(null)
  const [scan, scanState] = useTranslationSemanticMutation()
  errorToast(scanState.error, { title: "Semantic scan failed" })

  async function runSemanticScan() {
    const res = await scan()
    const data = res.data?.findSemanticDuplicates
    if (!data) return
    setScanMsg(data.Detail)
    const mapped: DuplicateGroup[] = data.Groups.map((group) => {
      const groupRows = group.Codes.map((code) =>
        board.rowsByCode.get(code),
      ).filter((row) => !!row)
      return {
        ...toDuplicateGroup(group.Codes.join(","), groupRows, "AI"),
        name: group.Name,
      }
    }).filter((group) => group.rows.length > 1)
    setAiGroups(mapped)
  }

  // AI groups first, then fuzzy; drop a fuzzy group already covered by an
  // AI one.
  const seen = new Set(
    aiGroups.map((group) =>
      group.rows
        .map((row) => row.code)
        .sort()
        .join("|"),
    ),
  )
  const groups = [...aiGroups]
  for (const group of fuzzyGroups) {
    const key = group.rows
      .map((row) => row.code)
      .sort()
      .join("|")
    if (!seen.has(key)) groups.push(group)
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      {scanMsg ? (
        <div className="mb-2 rounded-md border border-border p-2 text-xs text-muted-foreground">
          {scanMsg}
        </div>
      ) : null}
      <Table
        className={cn("translation-duplicates-table", className)}
        tableId="translation-duplicates"
        data={groups}
        columns={DUPLICATE_COLUMNS}
        getRowId={(group) => group.key}
        isCellsWrapped
        enableInfiniteScroll
        enableRowSelection={false}
        onRowClick={(row) => setSelected(row.original)}
        {...tableProps}
      >
        <Table.Toolbar>
          <Table.Search placeholder="Search text or code..." />
          <div className="flex-1" />
          <Button
            size="tiny"
            variant="secondary"
            data-feature-code="UI_BackofficeTranslation_Dedupe_SemanticScan"
            onClick={runSemanticScan}
            disabled={scanState.loading}
            title="Uses the AI to group texts that mean the same but are worded differently (e.g. 'City' / 'Municipality') — beyond the spelling-variant matches already listed. Runs a batch over all texts (spends tokens), so it's behind this button."
          >
            {scanState.loading ? "Scanning…" : "Find semantic duplicates (AI)"}
          </Button>
          <Table.ToolbarMenu exportFileName="duplicate-texts" />
        </Table.Toolbar>
        <Table.Content isLoading={loading} />
      </Table>

      <TranslationDuplicateGroupDialog
        group={selected}
        languages={board.languages}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}

export default TranslationDuplicatesView
